<template>
    <article ref="cardElement" class="setup-card">
        <div class="flex w-full flex-wrap items-start justify-between gap-2">
            <div class="tiles">
                <SetupCalendarTile />
                <SetupPerspectiveTile />
                <SetupProfileTile />
            </div>
            <slot name="dismiss" />
        </div>

        <div class="text">
            <h3 class="card-title">{{ title }}</h3>
            <p class="lede">{{ t("coachingSetup.home.controlDescription") }}</p>
        </div>

        <ul class="asks">
            <li v-for="step in card.steps" :key="step.id" class="ask" :class="{ 'is-done': step.done }">
                <span v-if="step.done" class="sr-only">{{ t("coachingSetup.home.done") }}</span
                >{{ step.label }}
            </li>
        </ul>

        <slot name="actions">
            <BaseLink :href="card.url" class="base-btn-primary cta" @click="handleClick">{{ t("coachingSetup.home.finish") }}</BaseLink>
        </slot>
        <slot name="feedback" />
    </article>
</template>

<script setup lang="ts">
import { guidedSetupHomeEventProps, useGuidedSetupHomeView } from "~vue/ChatHome/guidedSetupHomeAnalytics";
import SetupCalendarTile from "~vue/icons/SetupCalendarTile.vue";
import SetupPerspectiveTile from "~vue/icons/SetupPerspectiveTile.vue";
import SetupProfileTile from "~vue/icons/SetupProfileTile.vue";
import type { SetupHomeCard } from "~vue/ProfileSetup/types";
import BaseLink from "~vue/router/BaseLink.vue";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";
import { computed, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";

const { card, trackEvents = true } = defineProps<{ card: SetupHomeCard; trackEvents?: boolean }>();
const { t } = useI18n();
const cardElement = useTemplateRef<HTMLElement>("cardElement");

const title = computed(() => (card.completed_count === 0 ? t("coachingSetup.home.controlTitle") : t("coachingSetup.home.progressedTitle")));

if (trackEvents) {
    useGuidedSetupHomeView(cardElement, () => guidedSetupHomeEventProps(card));
}

function handleClick(): void {
    if (trackEvents) {
        void logUserInteraction(AmplitudeEventNames.CARD_CLICKED, guidedSetupHomeEventProps(card));
    }
}
</script>

<style scoped>
@reference "../../../css/tailwind.css";

@layer components {
    /* The footprint of the card this one replaces, so the row keeps its shape
       whichever of the two is offered. */
    .setup-card {
        @apply flex min-h-[305px] flex-col items-start gap-4 rounded-lgr border border-border bg-surface-raised p-5;
    }

    .tiles {
        @apply flex gap-2;
    }

    .text {
        @apply flex flex-col gap-1;
    }

    /* Not `.title`: that is a 30px display utility, and Tailwind's utilities layer
       outranks this one, so the class name alone would resize the heading. */
    .card-title {
        @apply title-body text-pretty text-ink;
    }

    .lede {
        @apply body-small text-ink-muted;
    }

    .asks {
        @apply flex list-disc flex-col gap-1 pl-5;
    }

    .ask {
        @apply body-small text-ink marker:text-ink-muted;
    }

    .is-done {
        @apply text-ink-muted line-through;
    }

    .cta {
        @apply mt-auto inline-flex w-full items-center justify-center;
    }
}
</style>
